// ─── VOUCHER PRODUK di keranjang — ringkasan angka (helper MURNI) ───────────
//
// Dipakai blok Voucher di /keranjang (CartVoucherSection) untuk menyusun
// "Harga Umum · Potongan Voucher · Diskon Level · Total" dari harga tiap
// baris (resolver checkout) + pratinjau server voucher terpilih. Tanpa I/O.

import type { PriceCut } from "@/lib/pricing";

export type CartVoucherPricedLine = {
  /** Kunci baris "<cart>:<baris>" — sama dengan kunci di pratinjau server. */
  key: string;
  qty: number;
  /** Harga jual umum; null = harga menyusul (tak masuk hitungan). */
  normalPrice: number | null;
  /** Harga PRA-voucher (sudah promo/level); null = harga menyusul. */
  unitPrice: number | null;
  /**
   * Potongan yang membentuk `unitPrice` (resolver checkout, mig 5920) — bahan
   * memisah "Potongan promo" dari "Diskon level" (audit A9). Absen = gabung.
   */
  cuts?: readonly PriceCut[] | null;
  /**
   * Tambahan varian (mig 7900) yang termasuk di `unitPrice`. "Harga Umum"
   * ringkasan = (umum + tambahan) × qty supaya umum − voucher − promo − level
   * = total tetap menjumlah; basis promo tetap harga umum (dasar).
   */
  priceExtra?: number | null;
};

export type CartVoucherPreviewLine = {
  key: string;
  eligible: boolean;
  /** Harga satuan SETELAH voucher (jawaban server). */
  unitPrice: number | null;
  /** Voucher per pcs baris ini (jawaban server) — basis potongan promo pasca-voucher. */
  perPcs?: number | null;
};

export type CartVoucherSelectionLike = {
  voucherName: string;
  preview: { amount: number; lines: readonly CartVoucherPreviewLine[] };
};

/** Angka ringkasan Harga Umum · Potongan Voucher · Potongan Promo · Diskon Level · Total. */
export type CartVoucherSummary = {
  /** Σ harga jual umum × qty (baris ber-harga umum saja). */
  grossTotal: number;
  voucherName: string | null;
  voucherAmount: number;
  /**
   * Σ potongan PROMO kampanye × qty (dari `cuts`, mig 5920) atas basis
   * pasca-voucher per baris; 0 bila tak ada baris ber-jejak promo.
   */
  promoDiscount: number;
  /** Σ (harga umum − harga akhir) × qty − voucher − promo = diskon level yang tersisa. */
  levelDiscount: number;
  /**
   * Potongan BUNDEL (mig 8040) — Σ (harga terpisah sesudah voucher − harga
   * bundel akhir) × qty; diisi `buildBundleCartVoucherSummary`
   * (`src/lib/bundleCheckout.ts`). Absen = keranjang tanpa bundel.
   */
  bundleDiscount?: number;
  /** Σ harga akhir × qty. */
  total: number;
  /** Ada baris "harga menyusul" — angkanya belum termasuk. */
  hasUnpriced: boolean;
};

/** Satu potongan atas sebuah basis — rumus & pembulatan = `applyPromo`. */
function applyCut(base: number, cut: PriceCut): number {
  const v = Number.isFinite(cut.value) ? Math.max(0, cut.value) : 0;
  return Math.max(0, Math.round(cut.mode === "nominal" ? base - v : base * (1 - v / 100)));
}

/**
 * Susun ringkasan. Baris LAYAK memakai harga akhir dari pratinjau server;
 * baris lain tetap harga pra-voucher. `promoDiscount` dihitung dari jejak
 * potongan tiap baris (promo harga coret = potongan pertama, atas basis
 * pasca-voucher — cermin mesin voucher dua tier), lalu `levelDiscount`
 * DITURUNKAN (bukan dijumlah dari tier) supaya keempat angka selalu SALING
 * MENJUMLAH: umum − voucher − promo − level = total, persis kolom nota.
 * Tanpa jejak, `promoDiscount` 0 dan seluruh sisanya tetap "diskon level /
 * promo" seperti sebelum audit A9.
 */
export function buildCartVoucherSummary(
  carts: ReadonlyArray<{ lines: readonly CartVoucherPricedLine[] }>,
  selection: CartVoucherSelectionLike | null
): CartVoucherSummary {
  let gross = 0;
  let total = 0;
  let promo = 0;
  let hasUnpriced = false;
  const finalByKey = new Map<string, number>();
  const perPcsByKey = new Map<string, number>();
  if (selection) {
    for (const l of selection.preview.lines) {
      if (l.eligible && l.unitPrice !== null && Number.isFinite(l.unitPrice)) {
        finalByKey.set(l.key, l.unitPrice);
        const v = Number(l.perPcs ?? 0);
        if (Number.isFinite(v) && v > 0) perPcsByKey.set(l.key, v);
      }
    }
  }
  for (const cart of carts) {
    for (const l of cart.lines) {
      const qty = Math.max(0, Math.floor(Number(l.qty) || 0));
      if (qty <= 0) continue;
      if (l.normalPrice === null || l.unitPrice === null) {
        hasUnpriced = true;
        continue;
      }
      const extra = Math.max(0, Number(l.priceExtra ?? 0) || 0);
      gross += (l.normalPrice + extra) * qty;
      total += (finalByKey.get(l.key) ?? l.unitPrice) * qty;
      const promoCut = l.cuts?.find((c) => c.source === "promo");
      if (promoCut) {
        const basis = Math.max(0, l.normalPrice - (perPcsByKey.get(l.key) ?? 0));
        promo += Math.max(0, basis - applyCut(basis, promoCut)) * qty;
      }
    }
  }
  const voucherAmount = selection ? Math.max(0, Math.floor(selection.preview.amount)) : 0;
  // Promo tak boleh melampaui jarak yang tersisa (harga keranjang lebih rendah
  // dari rantai = tak ada yang bisa diklaim promo).
  const promoDiscount = Math.min(Math.max(0, Math.round(promo)), Math.max(0, gross - voucherAmount - total));
  return {
    grossTotal: gross,
    voucherName: selection?.voucherName ?? null,
    voucherAmount,
    promoDiscount,
    levelDiscount: Math.max(0, gross - voucherAmount - promoDiscount - total),
    total,
    hasUnpriced
  };
}

/**
 * Baris `VoucherCartLine` (kelayakan instan `quoteProductVoucher`) dari hasil
 * `resolveCartsPricing` — dipakai blok voucher di /keranjang dan halaman
 * /keranjang/voucher supaya keduanya menghitung dari harga yang SAMA.
 */
export function voucherLinesFromPricing(
  pricing: ReadonlyArray<{
    lines: ReadonlyArray<{
      key: string;
      productId: string;
      qty: number;
      normalPrice: number | null;
      unitPrice: number | null;
      tier: import("@/lib/pricing").PriceTier;
      tier2?: import("@/lib/pricing").PriceTier;
      promoCampaignId: string | null;
      priceExtra?: number | null;
    }>;
  }>
): import("@/lib/voucher").VoucherCartLine[] {
  return pricing.flatMap((c) =>
    c.lines.map((l) => ({
      key: l.key,
      product: { id: l.productId },
      qty: l.qty,
      normalPrice: l.normalPrice,
      preVoucherUnitPrice: l.unitPrice,
      tier: l.tier,
      tier2: l.tier2 ?? null,
      promoCampaignId: l.promoCampaignId,
      priceExtra: l.priceExtra ?? null
    }))
  );
}
